import clsx from 'clsx';
import { Button } from 'ui';
import HeaderResponsive from 'core/modules/components/headers/HeaderResponsive/HeaderResponsive';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { MAIN_CONTENT_ID } from '../constants';
import useLayoutCalendarMobileMenu from './useLayoutCalendarMobileMenu';

const LayoutCalendar = ({
  children,
  header,
  footer,
  responsive,
  showMobileMenu,
  onShowMobileMenu,
  titleResponsive,
  onBack,
  asideLabel,
  isMainLandmark = true,
}) => {
  const { t } = useTranslation();
  const { asideId, asideRef, closeButtonRef, isDrawerOpen, menuButtonRef, rootRef } = useLayoutCalendarMobileMenu({
    isOpen: Boolean(responsive && showMobileMenu),
    isResponsive: Boolean(responsive),
    onToggle: onShowMobileMenu,
  });

  // == Solo el shell de página emite el landmark main; anidado renderiza un div para no
  // == duplicarlo ni duplicar el id del skip link.
  const ContentTag = isMainLandmark ? 'main' : 'div';
  const mainLandmarkProps = isMainLandmark ? { id: MAIN_CONTENT_ID, tabIndex: -1 } : {};

  return (
    <div
      className={clsx('layout-calendar', {
        'layout-calendar--with-footer': footer,
        'layout-calendar--with-header': header,
        'layout-calendar--responsive': responsive,
        'layout-calendar--responsive-open': isDrawerOpen,
      })}
      ref={rootRef}
    >
      {header && <header className="layout-calendar__header">{header}</header>}
      {responsive && (
        <HeaderResponsive
          isMenuOpen={isDrawerOpen}
          menuButtonRef={menuButtonRef}
          menuControlsId={asideId}
          onBack={onBack}
          onShowMobileMenu={onShowMobileMenu}
          title={titleResponsive}
        />
      )}
      <div className="layout-calendar__columns">
        <aside
          className="layout-calendar__column-1"
          aria-label={asideLabel || t('common:SecondaryPanelLabel')}
          id={responsive ? asideId : undefined}
          ref={asideRef}
        >
          {responsive && (
            <div className="layout-calendar_responsive-close-button">
              <Button
                ariaLabel={t('buttons:Close menu')}
                iconSize="xl"
                isIconOnly
                leftIcon="close"
                onClick={onShowMobileMenu}
                ref={closeButtonRef}
                variant="plain"
              />
            </div>
          )}
          {children?.[0]}
        </aside>
        <ContentTag className="layout-calendar__column-2" {...mainLandmarkProps}>
          <div className="layout-calendar__column-2-children">{children?.[1]}</div>
        </ContentTag>
      </div>
      {footer && <div className="layout-calendar__footer">{footer}</div>}
      {/* Mouse-only affordance: the X button is its keyboard/AT counterpart, so it stays out of
          the tab order and the accessibility tree instead of announcing a second full-screen
          "Close menu" control. */}
      {responsive && <button aria-hidden="true" className="layout-calendar__mobile-menu-backdrop" onClick={onShowMobileMenu} tabIndex={-1} type="button" />}
    </div>
  );
};

LayoutCalendar.propTypes = {
  asideLabel: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  header: PropTypes.node,
  isMainLandmark: PropTypes.bool,
  onBack: PropTypes.func,
  onShowMobileMenu: PropTypes.func,
  responsive: PropTypes.bool,
  showMobileMenu: PropTypes.bool,
  titleResponsive: PropTypes.string,
};

export default LayoutCalendar;
